import { createApi } from '@reduxjs/toolkit/query/react';
import { appPortalBaseQuery } from '@/features/baseQuery.ts';
import { FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import {
  DownloadTemplateRequest,
  InvestmentFileWorkflowResponse,
  InvestmentImportResultDto,
  InvestmentUploadDryRunParams,
  InvestmentUploadDryRunResponse,
  InvestmentUploadDryRunResultDto,
  InvestmentUploadResponse,
} from '@/features/investor-management/investments.def.ts';
import { investorManagementApi } from '@/features/investor-management/investor-management.api.ts';
import { invalidateAnalytics } from '@/features/analytics/invalidate-analytics.ts';

const EXCEL_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

type BaseQueryResult = { data?: Response; error?: FetchBaseQueryError; meta?: FetchBaseQueryMeta };

interface InvestmentUploadRequest {
  file: File;
}

interface InvestmentDryRunRequest extends InvestmentUploadRequest {
  params: InvestmentUploadDryRunParams;
}

/**
 * Extract filename from Content-Disposition header.
 * Falls back to `fallback` when the header is missing or unparseable.
 */
const extractFileName = (response: Response, fallback: string): string => {
  const disposition = response.headers.get('Content-Disposition');
  if (!disposition) return fallback;

  const quoted = disposition.match(/filename="(.+?)"/);
  if (quoted?.[1]) return quoted[1];

  const unquoted = disposition.match(/filename=([^\s;]+)/);
  if (unquoted?.[1]) return unquoted[1];

  return fallback;
};

const parseInvestmentFileWorkflowResponse = async <TSuccess>(
  response: Response,
): Promise<
  { data: InvestmentFileWorkflowResponse<TSuccess> } | { error: { status: number; data: unknown } }
> => {
  if (response.ok) {
    const data: TSuccess = await response.json();
    return { data: { type: 'success', data } };
  }

  const contentType = response.headers.get('Content-Type') ?? '';

  if (contentType.includes(EXCEL_CONTENT_TYPE)) {
    const blob = await response.blob();
    const fileName = extractFileName(response, 'InputError.xlsx');
    const downloadUrl = URL.createObjectURL(blob);
    return { data: { type: 'fileError', downloadUrl, fileName } };
  }

  const errorData = await response.json().catch(() => ({
    title: 'Upload Failed',
    status: response.status,
  }));
  return { error: { status: response.status, data: errorData } };
};

type ExecuteBaseQuery = (args: {
  url: string;
  method: 'POST';
  body: FormData;
  params?: Record<string, string | number>;
  responseHandler: (response: Response) => Promise<Response>;
  validateStatus: () => boolean;
}) => Promise<BaseQueryResult>;

const createInvestmentFileWorkflowQuery = async <TSuccess>(
  path: string,
  file: File,
  executeBaseQuery: ExecuteBaseQuery,
  params?: Record<string, string | number>,
) => {
  const formData = new FormData();
  formData.append('file', file);

  const result = await executeBaseQuery({
    url: path,
    method: 'POST',
    body: formData,
    params,
    responseHandler: async (response: Response) => response,
    validateStatus: () => true,
  });

  if (result.error) {
    return { error: result.error };
  }

  return parseInvestmentFileWorkflowResponse<TSuccess>(result.data!);
};

export const investmentsApi = createApi({
  reducerPath: 'investmentsApi',
  baseQuery: appPortalBaseQuery(),
  tagTypes: ['InvestmentUpload'],
  endpoints: (builder) => ({
    /**
     * Downloads the investment Excel workbook, pre-populated from live state: complete commitment
     * and redemption history always, NAV only when `includeNav` is true.
     *
     * Returns an Object URL string so the caller can trigger a file download with saveAs(). Auth is
     * handled by appPortalBaseQuery.
     *
     * The argument is optional so a paramless call keeps working; it resolves to `includeNav=false`,
     * which is the API's own default.
     */
    downloadTemplate: builder.query<string, DownloadTemplateRequest | void>({
      query: (request) => ({
        url: '/investments/template',
        method: 'GET',
        params: { includeNav: request?.includeNav ?? false },
        responseHandler: (response: Response) => response.blob(),
      }),
      transformResponse: (blob: Blob) => URL.createObjectURL(blob),
    }),

    /**
     * Uploads a completed investment Excel file.
     *
     * The backend returns ONE of:
     * - HTTP 200 + JSON  → InvestmentImportResultDto (success)
     * - HTTP 400 + .xlsx → annotated error file (file validation errors)
     * - HTTP 400 + JSON  → ProblemDetails (simple validation / bad file)
     *
     * We use a custom queryFn because we need to sniff Content-Type on
     * error responses to distinguish binary Excel from JSON ProblemDetails.
     * Auth is handled automatically by appPortalBaseQuery.
     */
    uploadInvestmentExcel: builder.mutation<InvestmentUploadResponse, InvestmentUploadRequest>({
      queryFn: async ({ file }, _api, _extraOptions, baseQuery) => {
        return createInvestmentFileWorkflowQuery<InvestmentImportResultDto>(
          '/investments/upload',
          file,
          (queryArgs) => baseQuery(queryArgs) as Promise<BaseQueryResult>,
        );
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.type === 'success') {
            // Bare tags throughout: the workbook can touch any number of investors, so every
            // cached per-investor entry of these types has to be invalidated. Commitment,
            // redemption and NAV rows feed the investment history table, the profile's
            // NetCommitment/LatestNav, and the activity history feed.
            dispatch(
              investorManagementApi.util.invalidateTags([
                'DashboardSummary',
                'DashboardRecords',
                'InvestmentHistory',
                'InvestorProfile',
                'InvestorActivityHistory',
              ]),
            );
            // NAV rows also feed the fund pages, the NAV breakdown and the month picker.
            invalidateAnalytics(dispatch);
          }
        } catch {
          // No cache updates on failed upload
        }
      },
    }),

    dryRunInvestmentExcel: builder.mutation<
      InvestmentUploadDryRunResponse,
      InvestmentDryRunRequest
    >({
      queryFn: async ({ file, params }, _api, _extraOptions, baseQuery) => {
        const queryParams: Record<string, string | number> = {
          page: params.page,
          pageSize: params.pageSize,
        };

        if (params.status) {
          queryParams.status = params.status;
        }

        if (params.geography) {
          queryParams.geography = params.geography;
        }

        if (params.searchTerm) {
          queryParams.searchTerm = params.searchTerm;
        }

        // Omitted rather than sent empty: the API reads an absent month as latest NAV and rejects
        // anything that is not ISO yyyy-MM.
        if (params.navAsOfMonth) {
          queryParams.navAsOfMonth = params.navAsOfMonth;
        }

        return createInvestmentFileWorkflowQuery<InvestmentUploadDryRunResultDto>(
          '/investments/upload/dry-run',
          file,
          (queryArgs) => baseQuery(queryArgs) as Promise<BaseQueryResult>,
          queryParams,
        );
      },
    }),
  }),
});

export const {
  useDryRunInvestmentExcelMutation,
  useLazyDownloadTemplateQuery,
  useUploadInvestmentExcelMutation,
} = investmentsApi;
